import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from "react";
import {
  fetchTvTitle,
  useTvCatalogue,
  type TvCatalogueHome,
  type TvCatalogueItem,
  type TvEpisode,
  type TvLocale,
  type TvRail,
  type TvTitleDetail,
} from "../../../hooks/useTvCatalogue";
import { playbackFailed } from "../../../lib/playbackTelemetry";
import { RemoteHint } from "../RemoteHint";
import { useScheme } from "../schemeContext";
import { usePlayReport } from "../player";
import StreamSurface from "../StreamSurface";
import { STREAM_FAILURE_MESSAGE, type StreamFailure } from "../streamPlayback";
import { useFocusGrid } from "../useFocusGrid";
import { useRailPages, type RailView } from "./useRailPages";
import {
  resolveTvPlayback,
  mintLicenceFor,
  TvPlaybackRefused,
  type TvPlayback,
} from "./vodPlayback";
import { vodCopy, type VodCopy } from "./vodCopy";

/**
 * The VOD screen: Foyer's own library, on the television (`TV-3`).
 *
 * **One renderer, nine hotel templates**, exactly as `../custom/CustomScreen.tsx`
 * and `../offers/OffersScreen.tsx` already established — a template supplies a
 * {@link VodScreenStyle} built from its own tokens and NOTHING ELSE. Nine VOD
 * pages would be nine keyboard models to debug on a remote, nine spinners that
 * never resolve, and nine places the episode picker has to be fixed. The scheme
 * supplies colour and type; this file supplies the layout and the interaction,
 * for all of them.
 *
 * Three levels and no more: **rails → title detail → episode picker → Play.**
 *
 * ## What is deliberately absent
 *
 * - **No search.** `asset/search/:query` and `asset/:id/similar` are stubs
 *   upstream that always answer empty (`docs/legacy-app-facade.md` §3). A search
 *   box on this screen would be a box that never finds anything — building
 *   search means building search, and it is not in v1.
 * - **No free layout.** Rails are rails. The "custom" screen type exists for
 *   operator-authored pages; a catalogue is not one.
 * - **No continue-watching.** `userplayhistory/lastviewedoffset` is a stub that
 *   echoes offset 0, and a resume row that always resumes at the beginning is
 *   worse than none.
 *
 * ## ⚠️ Empty is the common case, and it is written out in words
 *
 * Foyer production has **zero `packages` rows**. Until a premise is
 * granted a package every rail resolves to nothing — so the empty state is not
 * an edge case to hide behind a spinner. It is what most premises will show, and
 * it says so, with a next action. A black rectangle here reads as a broken
 * television, which is exactly the impression this player exists to remove.
 *
 * ## ⚠️ One playing surface, ever
 *
 * The hardware decoder pool on a hotel panel is single digits of instances and
 * an Android WebView does not release one just because a `<video>` left the DOM
 * (`docs/memory/global/pitfalls/panel-video-encoding.md`). So `StreamSurface` is
 * mounted on the `play` branch ONLY — never behind a hidden style, never kept
 * warm off-screen, never two at once. The surface already gives the decoder back
 * correctly on unmount; the only way to defeat that is to keep it mounted, and
 * nothing here does.
 *
 * ## Ten-foot navigation
 *
 * `useFocusGrid` throughout, one grid per level, handlers read through its ref
 * rather than a closure. Keys are `e.key` only — the Tizen keyCode bridge
 * normalises Samsung remotes upstream in `src/tv/lib/tizenKeys.ts`, so a second
 * keyCode table here would be a second thing to keep in step.
 */

export interface VodScreenStyle {
  /** Page ground behind the rails. */
  bg: string;
  /** Tile and panel surface. */
  card: string;
  /** Primary text. */
  ink: string;
  /** Secondary text — synopsis, meta lines. */
  inkDim: string;
  /** Hairline around tiles and between sections. */
  rule: string;
  /** Focus ring, rail headings, the Play button's fill. */
  accent: string;
  /** Text colour ON the accent fill. */
  onAccent: string;
  /** Display font for titles. */
  titleFont: string;
  /** Body font. */
  font: string;
  /** Corner radius the template uses on its own cards. */
  radius: number;
  /** Tone behind a tile with no artwork. */
  placeholder: string;
}

interface Props {
  style: VodScreenStyle;
  /** Leaves the screen — the Back key on the rails. */
  onBack: () => void;
  scopeRef: RefObject<HTMLElement>;
  /**
   * What the premise calls this screen. Blank falls back to the copy pack's own
   * word, so the heading is never empty.
   */
  title?: string;
  /**
   * Inner padding. A host that owns the whole viewport passes the TV-safe
   * constants (the default); a host that has already inset its sub-pages — the
   * launcher family's `LauncherPage` — passes zeroes, or the page is indented
   * twice and loses a third of its width.
   */
  pad?: { x: number; y: number };
  /**
   * Draw the remote-hint strip along the foot.
   *
   * On by default, because the launcher family's sub-pages carry no persistent
   * footer and a three-level screen has to say that Back exists. The bespoke
   * templates pass `false`: their own `Footer` already renders the same strip,
   * and two of them stacked reads as a rendering fault.
   */
  hints?: boolean;
}

/**
 * The TV-safe area, same constants as `CustomScreen` and `OffersScreen`.
 * Nothing meaningful may sit in the outer ~5% of a panel: the bezel eats it, and
 * which 5% depends on the television rather than on us.
 */
export const SAFE_X = 96;
export const SAFE_Y = 72;

/**
 * Tile geometry.
 *
 * Landscape rather than portrait posters, and that is a decision about the
 * hardware: a 2:3 poster tall enough to read at three metres leaves room for one
 * and a half rails on a 1080p panel inside the safe area, and a library a guest
 * has to scroll to discover has a second rail nobody ever sees. 16:9 fits two
 * whole rails and the top of a third, which is what makes the page read as a
 * library rather than as a list.
 */
const TILE_W = 320;
const TILE_H = 180;
const TILE_GAP = 28;
/** 1920 minus the safe area, over a 320 tile plus its gap — four and a sliver. */
const VISIBLE_TILES = 4;
/** Whole height of one rail block: heading, tiles, captions and the gap under. */
const RAIL_H = 352;
/** Rows of the episode list on screen at once before it slides. */
const VISIBLE_EPISODES = 4;
const EPISODE_H = 96;
/**
 * How long the "← Avsluta" chip stays over a playing title.
 *
 * It has to appear at all — nothing else on the player says how to leave. It
 * has to go away because it sits exactly where subtitles do, and a static bright
 * element held on a hotel panel all evening is what burn-in is.
 */
const HINT_LINGER_MS = 6_000;

type Phase = "rails" | "detail" | "play";

/**
 * The home rails, as the first tab.
 *
 * A sentinel rather than an empty string: `""` is what an id looks like when a
 * field is missing, and this value is compared against server-minted ids.
 */
const HOME_TAB = "__home__";

/** A sub-page's rails arrive whole, so nothing may ask for more of one. */
const NO_REACH = () => {};

interface PageTab {
  id: string;
  title: string;
}

/**
 * A sub-page's rails, in the shape the rail renderer already speaks.
 *
 * ⚠️ `hasMore: false` and `loading: false`, always. `tvSubPages` resolves each
 * page with `pageable: false`, so every rail arrives whole and there is no
 * route that would serve page 2 of one. Claiming otherwise would draw a
 * loading tile that never resolves.
 */
function pageRails(rails: readonly TvRail[]): RailView[] {
  return rails.map((rail) => ({
    carouselId: rail.carouselId,
    title: rail.title,
    items: rail.items,
    hasMore: false,
    loading: false,
  }));
}

export default function VodScreen({
  style,
  onBack,
  scopeRef,
  title,
  pad = { x: SAFE_X, y: SAFE_Y },
  hints = true,
}: Props) {
  const scheme = useScheme();
  const copy = vodCopy(scheme.context.locale);
  const loc = useMemo<TvLocale>(
    () => ({
      locale: scheme.context.locale ?? "en",
      defaultLocale:
        scheme.context.defaultLocale ?? scheme.context.locale ?? "en",
    }),
    [scheme.context.locale, scheme.context.defaultLocale]
  );

  // Same seam as the guide: the router already holds this, so take it. A
  // supplied catalogue and a design-preview fixture are the same thing to the
  // hook - data somebody already has - which is why it short-circuits the
  // fetch for both.
  const { home, unreachable, reload } = useTvCatalogue(
    scheme.context.premiseId,
    loc,
    scheme.vod
  );
  const { rails: allRails, reach } = useRailPages(home, loc);
  // ⚠️ A rail whose FIRST page came back empty is unavailable, and a named
  // heading with a void under it is worse than no heading. Dropped here, once,
  // so neither the focus grid nor the layout has to know about the case.
  const homeRails = useMemo(
    () => allRails.filter((r) => r.items.length > 0),
    [allRails]
  );

  /**
   * The menu, as tabs — home first, then every page the room resolves.
   *
   * These come from `/tv/subpages` WITH their rails already resolved for this
   * room, so opening one costs nothing on the wire. That is why the tab bar can
   * switch on the arrow rather than on OK: there is no request to regret.
   */
  const tabs = useMemo<PageTab[]>(
    () => [
      { id: HOME_TAB, title: copy.homeTab },
      ...(home?.subPages ?? []).map((p) => ({
        id: p.subPageId,
        title: p.title,
      })),
    ],
    [home?.subPages, copy.homeTab]
  );
  const [activeTab, setActiveTab] = useState(HOME_TAB);

  // A page that vanished under the guest — the library re-polls, and an
  // operator can take a page out of the menu while somebody is standing in it.
  // Falling back to home beats rendering a tab that is no longer offered.
  const active = tabs.some((t) => t.id === activeTab) ? activeTab : HOME_TAB;
  const activePage = (home?.subPages ?? []).find((p) => p.subPageId === active);

  const rails = useMemo(
    () => (activePage ? pageRails(activePage.rails) : homeRails),
    [activePage, homeRails]
  );
  // Only the home rails page. A sub-page's rails arrive whole, so asking for
  // more would be a request nothing serves.
  const reachFor = activePage ? NO_REACH : reach;

  // Which title is open, held by ID rather than by index: the library re-polls
  // on its own timer and an index would silently re-point at a different film
  // the moment a rail changed underneath the guest.
  const [openId, setOpenId] = useState<string | null>(null);
  const [playing, setPlaying] = useState<TvCatalogueItem | null>(null);

  const phase: Phase = playing ? "play" : openId ? "detail" : "rails";

  const frame: CSSProperties = {
    display: "flex",
    flexDirection: "column",
    flex: "1 1 auto",
    minHeight: 0,
    width: "100%",
    boxSizing: "border-box",
    padding: `${pad.y}px ${pad.x}px`,
    background: style.bg,
    color: style.ink,
    fontFamily: style.font,
    overflow: "hidden",
  };

  if (phase === "play" && playing) {
    return (
      <div
        style={{ ...frame, padding: 0, position: "relative" }}
        data-testid="vod-screen"
      >
        <PlayerStage
          item={playing}
          style={style}
          copy={copy}
          scopeRef={scopeRef}
          hints={hints}
          onStop={() => setPlaying(null)}
        />
      </div>
    );
  }

  if (phase === "detail" && openId) {
    return (
      <div style={frame} data-testid="vod-screen">
        <TitleDetail
          // Remounted per title, so a guest who backs out and opens another one
          // never inherits the previous page's season or its focus.
          key={openId}
          assetId={openId}
          loc={loc}
          style={style}
          copy={copy}
          scopeRef={scopeRef}
          hints={hints}
          onPlay={setPlaying}
          onClose={() => setOpenId(null)}
        />
      </div>
    );
  }

  return (
    <div style={frame} data-testid="vod-screen">
      <Heading
        style={style}
        eyebrow={copy.eyebrow}
        title={title || copy.title}
      />
      <RailsBody
        home={home}
        rails={rails}
        onPage={activePage !== undefined}
        tabs={tabs}
        activeTab={active}
        onTab={setActiveTab}
        unreachable={unreachable}
        style={style}
        copy={copy}
        scopeRef={scopeRef}
        reach={reachFor}
        onOpen={(item) => setOpenId(item.assetId)}
        onRetry={reload}
        onBack={onBack}
      />
      {hints && (
        <Hints
          style={style}
          items={
            rails.length === 0
              ? [
                  // OK retries only when there is something to retry. Offering
                  // it on an empty-but-reachable library would promise that
                  // pressing something changes the answer, and it does not.
                  ...(unreachable ? [{ key: "OK", label: copy.retry }] : []),
                  { key: "←", label: copy.hint.back },
                ]
              : [
                  { key: "▲ ▼ ◄ ►", label: copy.hint.move },
                  { key: "OK", label: copy.hint.open },
                  { key: "←", label: copy.hint.back },
                ]
          }
        />
      )}
    </div>
  );
}

// ── the rails ───────────────────────────────────────────────────────────────

function RailsBody({
  home,
  rails,
  onPage,
  tabs,
  activeTab,
  onTab,
  unreachable,
  style,
  copy,
  scopeRef,
  reach,
  onOpen,
  onRetry,
  onBack,
}: {
  home: TvCatalogueHome | undefined;
  rails: RailView[];
  /** True when a sub-page is open rather than the home rails. */
  onPage: boolean;
  tabs: PageTab[];
  activeTab: string;
  onTab: (id: string) => void;
  unreachable: boolean;
  style: VodScreenStyle;
  copy: VodCopy;
  scopeRef: RefObject<HTMLElement>;
  reach: (carouselId: string, index: number) => void;
  onOpen: (item: TvCatalogueItem) => void;
  onRetry: () => void;
  onBack: () => void;
}) {
  /**
   * The tab bar is ROW 0 of the same grid, not a second focus zone.
   *
   * `TvShell` runs two panes with a `railFocused` boolean, an `enabled` gate on
   * each hook and an effect that re-`.focus()`es the owning node — and its own
   * comments record three separate bugs that arrangement caused. A tab bar is
   * one row of buttons above a stack of rails, which is exactly what one grid
   * already expresses: Up out of the first rail lands on the tabs, Down off the
   * tabs lands in the first rail, and neither direction needs a handover.
   *
   * Hidden entirely when the room has no pages, so a premise that never used
   * the menu sees the screen it has always seen.
   */
  const hasTabs = tabs.length > 1;
  const tabRows = hasTabs ? 1 : 0;
  const rows = tabRows + Math.max(1, rails.length);
  /**
   * Columns are the LONGEST rail's length, and the ring is clamped back into
   * range by the effect below.
   *
   * Rails are not a grid — a forty-title rail beside a three-title one is
   * normal — and `useFocusGrid` takes one `cols` for the whole thing. Sizing it
   * to the focused rail is the obvious answer and the wrong one: `cols` would
   * have to be known before the hook that reports focus has run, which costs a
   * render of lag on every vertical move and lands ► on the wrong tile for one
   * frame. Sizing to the longest and correcting afterwards has no lag, and the
   * correction is a single clamp.
   *
   * ⚠️ `wrap: false` for the same reason: a library that jumps from the end of
   * a rail back to its beginning reads as a fault to somebody holding a remote,
   * and with a clamped over-wide grid it would jump to a *different* tile each
   * time depending on which rail was focused.
   */
  const cols = Math.max(
    1,
    ...rails.map((entry) => entry.items.length),
    hasTabs ? tabs.length : 1
  );
  const [focus, setFocus] = useFocusGrid({
    rows,
    cols,
    scopeRef,
    wrap: false,
    onEnter: (f) => {
      // OK on a tab moves INTO the page rather than committing a choice: the
      // arrow already switched it. Anything else would make the bar a
      // two-press control for a one-press job.
      if (hasTabs && f.r === 0) {
        setFocus({ r: 1, c: 0 });
        return;
      }
      const item = rails[f.r - tabRows]?.items[f.c];
      if (item) onOpen(item);
      // Nothing to open means the empty state has the screen, where OK is the
      // retry — and only when there is something to retry.
      else if (rails.length === 0 && unreachable) onRetry();
    },
    /**
     * BACK off a page returns to the home tab before it leaves the library.
     *
     * The same contract `TvShell` states for its sections — Back from a
     * section goes to the menu, Back from the menu leaves — so a guest who has
     * stepped into "Barn" does not get thrown out of the whole library by one
     * press they meant as "up one level".
     */
    onBack: () => {
      if (onPage) {
        onTab(HOME_TAB);
        setFocus({ r: 0, c: 0 });
        return;
      }
      onBack();
    },
  });

  // The grid holds its own index across a library that shrank under it, and a
  // short rail below a long one leaves the column out of range. Clamped for
  // READING; the correction below fixes the grid's own state.
  const onTabRow = hasTabs && focus.r === 0;
  const r = Math.min(
    Math.max(0, focus.r - tabRows),
    Math.max(0, rails.length - 1)
  );
  const rail = rails[r];
  const c = onTabRow
    ? Math.min(focus.c, tabs.length - 1)
    : Math.min(focus.c, Math.max(0, (rail?.items.length ?? 1) - 1));

  /**
   * 🚨 The tab switches on the ARROW, not on OK.
   *
   * `TvShell` pins the same rule for its sections — "switches section on the
   * arrow itself, without waiting for OK" — on the grounds that two presses per
   * move is what makes a ten-foot menu feel slow. The tabs in `AtelierScheme`'s
   * Info panel do the opposite and require OK; both contracts are argued for in
   * comments, and this surface follows the menu one because that is what it is.
   *
   * Safe here only because every page's rails arrived with the page: switching
   * costs no request, so there is nothing to regret having started.
   */
  useEffect(() => {
    if (!onTabRow) return;
    const next = tabs[c];
    if (next && next.id !== activeTab) onTab(next.id);
  }, [onTabRow, c, tabs, activeTab, onTab]);

  /**
   * 🚨 When the MENU changes under the guest, the ring follows the page — not
   * the slot.
   *
   * An operator can drop a page mid-visit; the library re-polls every ten
   * minutes. Without this, focus stayed at index 1 and the effect above
   * happily re-pointed it at whatever now occupied that slot: a guest standing
   * in "Barn" would find themselves in "Nyheter" with the ring exactly where
   * they left it and no press of their own to explain it. Snapping to wherever
   * the active page now sits — home, once the resolver has fallen back — is
   * the only reading that a person can account for.
   *
   * Runs ONLY on a genuine change of the tab set, compared by id: on every
   * render it would fight the arrow, which is the same effect one frame later.
   */
  const menuKey = tabs.map((t) => t.id).join("\u0000");
  const lastMenuKey = useRef(menuKey);
  useEffect(() => {
    if (lastMenuKey.current === menuKey) return;
    lastMenuKey.current = menuKey;
    if (!onTabRow) return;
    const i = tabs.findIndex((t) => t.id === activeTab);
    if (i >= 0 && i !== focus.c) setFocus({ r: 0, c: i });
  }, [menuKey, tabs, activeTab, onTabRow, focus.c, setFocus]);

  // Put the grid's OWN focus back in range — both axes.
  //
  // The column, because moving DOWN onto a shorter rail leaves the ring past its
  // end. And the ROW, because `useFocusGrid` never re-clamps stored focus when
  // `rows` shrinks: after a home poll drops a rail, a guest sitting on the last
  // one would see the ring drawn on the new last rail (the render clamps) while
  // OK read `rails[2]` and did nothing, and their first ArrowUp would be
  // swallowed moving from a row that is not there to one that clamps to the
  // same place. Reading clamped and pressing unclamped is the bug; correcting
  // the state is the fix.
  useEffect(() => {
    // The tab row is its own width and is never clamped to a rail's length.
    if (onTabRow) {
      if (focus.c > tabs.length - 1) setFocus({ r: 0, c: tabs.length - 1 });
      return;
    }
    if (!rail) return;
    const maxR = tabRows + Math.max(0, rails.length - 1);
    const maxC = Math.max(0, rail.items.length - 1);
    if (focus.r > maxR || focus.c > maxC) {
      setFocus({ r: Math.min(focus.r, maxR), c: Math.min(focus.c, maxC) });
    }
  }, [
    onTabRow,
    tabs.length,
    tabRows,
    rail,
    rails.length,
    focus.c,
    focus.r,
    setFocus,
  ]);

  // Ask for the next page as focus approaches the end of the focused rail. In
  // an effect rather than in the key handler so that a mouse hover, a clamp and
  // a fresh home read all reach the same place.
  useEffect(() => {
    if (!onTabRow && rail) reach(rail.carouselId, c);
  }, [onTabRow, rail, c, reach]);

  // ⚠️ ORDER MATTERS, and getting it wrong is the forever-spinner.
  //
  // Three sentences for three situations: "still asking" is a wait that will
  // end, "could not reach the library" is something somebody can act on, and
  // "this hotel has no library yet" is a fact about the contract. `unreachable`
  // is tested FIRST because it is precisely the case where `home` is still
  // undefined — putting the loading branch above it means a screen that has
  // given up hoping goes on saying "loading" until somebody reboots it, which is
  // the exact failure this player exists to remove. (The read only ever reports
  // unreachable while it holds nothing, so a last-good library still wins.)
  if (unreachable) {
    return <Note style={style}>{copy.unreachable}</Note>;
  }
  if (!home) {
    return <Note style={style}>{copy.loading}</Note>;
  }
  /**
   * ⚠️ An empty PAGE is not an empty library, and it must keep the tab bar.
   *
   * `copy.empty` says this hotel has no library — a fact about the contract.
   * Saying it because one menu page happens to resolve to nothing here would
   * be a lie told over a library that is working, and worse, returning a bare
   * Note would unmount the tabs and strand the guest on a screen with no way
   * back to them.
   */
  if (rails.length === 0 && onPage) {
    return (
      <div style={{ flex: "1 1 auto", minHeight: 0, overflow: "hidden" }}>
        <PageTabs
          tabs={tabs}
          activeTab={activeTab}
          focusedIndex={onTabRow ? c : -1}
          style={style}
          onHover={(i) => setFocus({ r: 0, c: i })}
        />
        <Note style={style}>{copy.emptyPage}</Note>
      </div>
    );
  }
  if (rails.length === 0) {
    return <Note style={style}>{copy.empty}</Note>;
  }

  // Keep the focused rail on screen without a scroll container: the stack slides
  // under a clipped window. `scrollIntoView` fights a d-pad on a panel whose
  // smooth-scroll lands a frame late, and a native scrollbar has no place on a
  // television.
  // `r` is already the RAIL index (the tab row is subtracted above), so the
  // slide is unchanged by the bar's presence — the bar scrolls away with the
  // first rail rather than pinning, which is what keeps a 1080-high panel from
  // spending a fifth of its screen on chrome.
  const shiftY = Math.max(0, r - 1) * RAIL_H;

  return (
    <div style={{ flex: "1 1 auto", minHeight: 0, overflow: "hidden" }}>
      {hasTabs && (
        <PageTabs
          tabs={tabs}
          activeTab={activeTab}
          focusedIndex={onTabRow ? c : -1}
          style={style}
          onHover={(i) => setFocus({ r: 0, c: i })}
        />
      )}
      <div
        style={{
          transform: `translateY(${-shiftY}px)`,
          transition: "transform 260ms cubic-bezier(0.2,0.8,0.2,1)",
        }}
      >
        {rails.map((entry, i) => (
          <Rail
            // Keyed by PAGE and rail: two pages may legitimately name the same
            // carousel, and without the page in the key React would reuse the
            // old page's row and its scroll offset.
            key={`${activeTab}:${entry.carouselId}`}
            rail={entry}
            style={style}
            copy={copy}
            focusedIndex={!onTabRow && i === r ? c : -1}
            onHover={(index) => setFocus({ r: i + tabRows, c: index })}
            onPick={onOpen}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * The menu, as a row of pills above the rails.
 *
 * ACTIVE and FOCUSED are drawn differently and mean different things: active is
 * the page on screen, focused is where the ring is. On this surface they move
 * together — the arrow switches the page — but they come apart the moment focus
 * drops into a rail, and a bar that only knew "selected" would leave the ring
 * behind on a tab nobody is standing on.
 */
function PageTabs({
  tabs,
  activeTab,
  focusedIndex,
  style,
  onHover,
}: {
  tabs: PageTab[];
  activeTab: string;
  focusedIndex: number;
  style: VodScreenStyle;
  onHover: (index: number) => void;
}) {
  return (
    <div
      data-testid="vod-page-tabs"
      style={{
        display: "flex",
        gap: 14,
        flex: "0 0 auto",
        marginBottom: 26,
        overflow: "hidden",
      }}
    >
      {tabs.map((tab, i) => {
        const active = tab.id === activeTab;
        const focused = i === focusedIndex;
        return (
          <button
            key={tab.id}
            type="button"
            data-testid={`vod-page-tab-${tab.id}`}
            data-active={active ? "true" : undefined}
            data-focused={focused ? "true" : undefined}
            aria-current={active ? "page" : undefined}
            onMouseEnter={() => onHover(i)}
            style={{
              flex: "0 0 auto",
              padding: "10px 26px",
              borderRadius: 999,
              border: "none",
              cursor: "pointer",
              font: "inherit",
              fontSize: 22,
              fontWeight: active ? 700 : 500,
              background: active ? style.accent : "transparent",
              color: active ? style.onAccent : style.inkDim,
              // The ring, drawn inside so a focused pill does not shift the row.
              boxShadow: focused
                ? `inset 0 0 0 3px ${active ? style.onAccent : style.accent}`
                : "none",
              transition: "background 160ms ease, color 160ms ease",
            }}
          >
            {tab.title}
          </button>
        );
      })}
    </div>
  );
}

function Rail({
  rail,
  style,
  copy,
  focusedIndex,
  onHover,
  onPick,
}: {
  rail: RailView;
  style: VodScreenStyle;
  copy: VodCopy;
  /** -1 when this rail does not hold the ring. */
  focusedIndex: number;
  onHover: (index: number) => void;
  onPick: (item: TvCatalogueItem) => void;
}) {
  const anchor = Math.max(0, focusedIndex);
  // The window stops at the last screenful rather than running the rail off the
  // left edge: a guest on the final tile should see the tiles before it, not
  // one tile beside three empty columns.
  const maxShift = Math.max(0, rail.items.length - VISIBLE_TILES);
  const shiftX =
    Math.min(Math.max(0, anchor - 1), maxShift) * (TILE_W + TILE_GAP);
  return (
    <section
      data-testid={`vod-rail-${rail.carouselId}`}
      style={{ height: RAIL_H, boxSizing: "border-box", overflow: "hidden" }}
    >
      <h2
        style={{
          margin: "0 0 12px",
          fontFamily: style.titleFont,
          fontWeight: 600,
          fontSize: 26,
          // Never below 1.2 inside a clipping box — see
          // docs/memory/global/pitfalls/tv-tight-line-height-clipping.md.
          lineHeight: 1.3,
          letterSpacing: "0.01em",
          color: style.ink,
        }}
      >
        {rail.title}
      </h2>
      <div style={{ overflow: "hidden" }}>
        <div
          style={{
            display: "flex",
            gap: TILE_GAP,
            transform: `translateX(${-shiftX}px)`,
            transition: "transform 220ms cubic-bezier(0.2,0.8,0.2,1)",
          }}
        >
          {rail.items.map((item, i) => (
            <Tile
              key={`${item.assetId}-${i}`}
              item={item}
              style={style}
              copy={copy}
              focused={focusedIndex === i}
              onMouseEnter={() => onHover(i)}
              onClick={() => onPick(item)}
            />
          ))}
          {rail.loading && (
            // A tile-shaped placeholder rather than nothing: the rail visibly
            // continues, so a guest at the end knows more is coming instead of
            // wondering whether they hit a wall. Not focusable — the grid's
            // columns are the real items only.
            <div
              aria-hidden
              data-testid={`vod-rail-more-${rail.carouselId}`}
              style={{
                flex: `0 0 ${TILE_W}px`,
                height: TILE_H,
                borderRadius: style.radius,
                background: style.placeholder,
                opacity: 0.5,
              }}
            />
          )}
        </div>
      </div>
    </section>
  );
}

function Tile({
  item,
  style,
  copy,
  focused,
  onMouseEnter,
  onClick,
}: {
  item: TvCatalogueItem;
  style: VodScreenStyle;
  copy: VodCopy;
  focused: boolean;
  onMouseEnter: () => void;
  onClick: () => void;
}) {
  // Year and length when the catalogue knows them; otherwise whatever second
  // line it does have — the series name on an episode, a tagline on a film. One
  // line either way, so tiles in a rail never sit at different heights, which
  // reads as a rendering fault rather than as missing metadata.
  const meta =
    [
      item.year ? String(item.year) : "",
      item.durationSeconds ? copy.duration(item.durationSeconds) : "",
    ]
      .filter(Boolean)
      .join(" · ") || item.subtitle;
  return (
    <button
      type="button"
      data-testid={`vod-tile-${item.assetId}`}
      aria-current={focused ? "true" : undefined}
      onMouseEnter={onMouseEnter}
      onClick={onClick}
      style={{
        appearance: "none",
        flex: `0 0 ${TILE_W}px`,
        width: TILE_W,
        textAlign: "left",
        display: "flex",
        flexDirection: "column",
        gap: 10,
        padding: 0,
        border: "none",
        background: "transparent",
        color: style.ink,
        fontFamily: "inherit",
        cursor: "pointer",
      }}
    >
      <div
        aria-hidden
        style={{
          height: TILE_H,
          borderRadius: style.radius,
          border: `1px solid ${focused ? style.accent : style.rule}`,
          background: item.imageUrl
            ? `url(${item.imageUrl}) center/cover`
            : style.placeholder,
          // A ring rather than a size change: tiles that grow and shrink make
          // the whole rail jump on every arrow press.
          boxShadow: focused ? `0 0 0 5px ${style.accent}55` : "none",
          transition: "box-shadow 160ms ease, border-color 160ms ease",
        }}
      />
      <div
        style={{
          fontFamily: style.titleFont,
          fontSize: 22,
          fontWeight: 600,
          // ⚠️ 1.3, not tighter. This box clips (`-webkit-line-clamp`), and a
          // tight line-height slices the descenders off g/y/p/j/q — poster
          // titles are exactly the shape that bites. See the pitfall note.
          lineHeight: 1.3,
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          color: focused ? style.accent : style.ink,
        }}
      >
        {item.title}
      </div>
      {meta && (
        <div style={{ fontSize: 18, lineHeight: 1.4, color: style.inkDim }}>
          {meta}
        </div>
      )}
    </button>
  );
}

// ── the title detail, and the episode picker inside it ──────────────────────

/**
 * One title, read at length — and the one press that starts it.
 *
 * The episode picker lives HERE rather than in a fourth component, for the
 * reason `OfferDetail` gives about the booking flow: nine templates share one
 * keyboard model only if there is one place that owns it. A series is a season
 * chip row plus an episode list; a film is a single Play button. Same grid, same
 * keys, one level of nesting.
 */
function TitleDetail({
  assetId,
  loc,
  style,
  copy,
  scopeRef,
  hints,
  onPlay,
  onClose,
}: {
  assetId: string;
  loc: TvLocale;
  style: VodScreenStyle;
  copy: VodCopy;
  scopeRef: RefObject<HTMLElement>;
  hints: boolean;
  onPlay: (item: TvCatalogueItem) => void;
  onClose: () => void;
}) {
  const [detail, setDetail] = useState<TvTitleDetail | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "missing">(
    "loading"
  );
  const [seasonIdx, setSeasonIdx] = useState(0);

  useEffect(() => {
    let alive = true;
    setState("loading");
    void (async () => {
      try {
        const next = await fetchTvTitle(assetId, loc);
        if (!alive) return;
        setDetail(next);
        setState(next ? "ready" : "missing");
      } catch {
        if (!alive) return;
        // Named, never a spinner that outlives the guest's patience. A detail
        // page that cannot load is the same dead end as a stream that cannot
        // start, and it gets the same treatment: a sentence.
        setState("missing");
      }
    })();
    return () => {
      alive = false;
    };
  }, [assetId, loc]);

  const seasons = detail?.seasons ?? [];
  const season = seasons[Math.min(seasonIdx, Math.max(0, seasons.length - 1))];
  const episodes = season?.episodes ?? [];
  const isSeries = seasons.length > 0;
  /** Row 0 is the season chips only when there is a genuine choice to make. */
  const seasonRow = seasons.length > 1 ? 0 : -1;
  const listOffset = seasonRow === 0 ? 1 : 0;
  const rows = isSeries ? listOffset + Math.max(1, episodes.length) : 1;
  const cols = Math.max(1, seasons.length);

  const play = useCallback((item: TvCatalogueItem) => onPlay(item), [onPlay]);

  // ⚠️ NEVER `enabled: state === "ready"`.
  //
  // `useFocusGrid` returns before binding its listener when disabled, and on the
  // detail branch there is no OTHER grid mounted — the rails are unmounted and
  // the player does not exist. So gating this on `ready` removed the keydown
  // handler from the whole subtree while a title was loading or had failed:
  // "Hämtar…" on screen and Back inert, which is the forever-spinner this file
  // exists to remove, made inescapable. The grid stays bound in every state;
  // `onEnter` refuses instead, which is a decision about what OK MEANS rather
  // than about whether the remote works at all.
  const [focus, setFocus] = useFocusGrid({
    rows,
    cols,
    scopeRef,
    wrap: true,
    onEnter: (f) => {
      if (!detail) return;
      if (!isSeries) {
        play({
          assetId: detail.assetId,
          title: detail.title,
          kind: detail.kind,
        });
        return;
      }
      if (f.r === seasonRow) {
        setSeasonIdx(Math.min(f.c, seasons.length - 1));
        return;
      }
      const episode = episodes[f.r - listOffset];
      if (episode) play(episode);
    },
    onBack: onClose,
  });

  // A season change resizes the list under the ring, so the ring would
  // otherwise sit on an episode row that no longer exists. Row 0 is the chip
  // row when there is a season choice and the first episode when there is not,
  // so it is the right landing place either way.
  useEffect(() => {
    setFocus({ r: 0, c: seasonIdx });
    // `setFocus` is stable; re-running this on it would fight the guest.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seasonIdx]);

  if (state === "loading") {
    return <Note style={style}>{copy.loading}</Note>;
  }
  if (state === "missing" || !detail) {
    return <Note style={style}>{copy.unreachable}</Note>;
  }

  const episodeIdx = focus.r - listOffset;
  const shiftY =
    Math.max(0, Math.min(episodeIdx, episodes.length - 1) - 1) * EPISODE_H;
  const art = detail.backdropUrl ?? detail.imageUrl;
  const meta = [
    copy.kind[detail.kind],
    detail.year ? String(detail.year) : "",
    detail.durationSeconds && !isSeries
      ? copy.duration(detail.durationSeconds)
      : "",
    detail.rating ?? "",
    detail.genres.slice(0, 3).join(", "),
  ].filter(Boolean);

  return (
    <div
      data-testid={`vod-detail-${detail.assetId}`}
      style={{
        flex: "1 1 auto",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        gap: 18,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          flex: "1 1 auto",
          minHeight: 0,
          display: "flex",
          gap: 44,
          overflow: "hidden",
        }}
      >
        <div
          aria-hidden
          style={{
            flex: "0 0 620px",
            borderRadius: style.radius,
            border: `1px solid ${style.rule}`,
            background: art ? `url(${art}) center/cover` : style.placeholder,
          }}
        />
        <div
          style={{
            flex: "1 1 auto",
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            gap: 16,
            overflow: "hidden",
          }}
        >
          <h2
            style={{
              margin: 0,
              fontFamily: style.titleFont,
              fontSize: 52,
              fontWeight: 600,
              lineHeight: 1.2,
              letterSpacing: "-0.01em",
            }}
          >
            {detail.title}
          </h2>
          {meta.length > 0 && (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 16,
                fontSize: 22,
                fontWeight: 600,
                lineHeight: 1.35,
                color: style.accent,
              }}
            >
              {/* Keyed by position, not by value: these come from five
                  unrelated fields and two of them can legitimately produce the
                  same string (a genre called "Film", a rating that is a year),
                  which would collide as a React key. */}
              {meta.map((m, i) => (
                <span key={`${i}-${m}`}>{m}</span>
              ))}
            </div>
          )}
          {detail.synopsis && (
            <div
              style={{
                fontSize: 24,
                lineHeight: 1.45,
                color: style.inkDim,
                display: "-webkit-box",
                WebkitLineClamp: 5,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {detail.synopsis}
            </div>
          )}
          {/* CONTENT-26: who supplied it, in their own words. A line in the
              text column - nothing here competes for a corner of the
              picture, which is why this surface came first. */}
          {detail.credit && (
            <div
              data-testid="vod-detail-credit"
              style={{
                fontSize: 18,
                lineHeight: 1.4,
                color: style.inkDim,
              }}
            >
              <div>
                {detail.credit.credits ?? copy.creditFrom(detail.credit.name)}
              </div>
              {detail.credit.copyright && (
                <div style={{ fontSize: 15, marginTop: 4 }}>
                  {detail.credit.copyright}
                </div>
              )}
            </div>
          )}

          {isSeries ? (
            <>
              {seasons.length > 1 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
                  {seasons.map((s, i) => (
                    <Chip
                      key={s.seasonId}
                      testid={`vod-season-${s.seasonId}`}
                      style={style}
                      selected={i === seasonIdx}
                      focused={focus.r === seasonRow && focus.c === i}
                      onHover={() => setFocus({ r: 0, c: i })}
                      onPress={() => setSeasonIdx(i)}
                    >
                      {s.title || copy.seasonLabel(s.number, i)}
                    </Chip>
                  ))}
                </div>
              )}
              <div
                style={{
                  fontSize: 17,
                  letterSpacing: "0.28em",
                  textTransform: "uppercase",
                  color: style.accent,
                  fontWeight: 600,
                  lineHeight: 1.4,
                }}
              >
                {copy.episodes}
              </div>
              <div
                style={{
                  flex: "1 1 auto",
                  minHeight: 0,
                  maxHeight: VISIBLE_EPISODES * EPISODE_H,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    transform: `translateY(${-shiftY}px)`,
                    transition: "transform 200ms cubic-bezier(0.2,0.8,0.2,1)",
                  }}
                >
                  {episodes.map((ep, i) => (
                    <EpisodeRow
                      key={ep.assetId}
                      episode={ep}
                      index={i}
                      style={style}
                      copy={copy}
                      focused={focus.r === i + listOffset}
                      onHover={() => setFocus({ r: i + listOffset, c: 0 })}
                      onPress={() => play(ep)}
                    />
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div style={{ marginTop: "auto", paddingTop: 8 }}>
              <ActionButton
                testid="vod-play"
                style={style}
                focused={focus.r === 0}
                onPress={() =>
                  play({
                    assetId: detail.assetId,
                    title: detail.title,
                    kind: detail.kind,
                  })
                }
              >
                {copy.play}
              </ActionButton>
            </div>
          )}
        </div>
      </div>
      {hints && (
        <Hints
          style={style}
          items={[
            { key: "▲ ▼", label: copy.hint.move },
            { key: "OK", label: copy.hint.play },
            { key: "←", label: copy.hint.back },
          ]}
        />
      )}
    </div>
  );
}

function EpisodeRow({
  episode,
  index,
  style,
  copy,
  focused,
  onHover,
  onPress,
}: {
  episode: TvEpisode;
  index: number;
  style: VodScreenStyle;
  copy: VodCopy;
  focused: boolean;
  onHover: () => void;
  onPress: () => void;
}) {
  return (
    <button
      type="button"
      data-testid={`vod-episode-${episode.assetId}`}
      aria-current={focused ? "true" : undefined}
      onMouseEnter={onHover}
      onClick={onPress}
      style={{
        appearance: "none",
        display: "flex",
        alignItems: "center",
        gap: 20,
        width: "100%",
        height: EPISODE_H,
        boxSizing: "border-box",
        padding: "0 18px",
        textAlign: "left",
        border: `1px solid ${focused ? style.accent : "transparent"}`,
        borderRadius: style.radius,
        background: focused ? style.card : "transparent",
        color: style.ink,
        fontFamily: "inherit",
        cursor: "pointer",
        overflow: "hidden",
      }}
    >
      <span
        style={{
          flex: "0 0 auto",
          minWidth: 56,
          fontSize: 20,
          fontWeight: 700,
          lineHeight: 1.4,
          color: style.accent,
        }}
      >
        {episode.episodeNumber ?? index + 1}
      </span>
      <span style={{ flex: "1 1 auto", minWidth: 0 }}>
        <span
          style={{
            display: "block",
            fontFamily: style.titleFont,
            fontSize: 24,
            fontWeight: 600,
            // Clipped by the row, so never below 1.2 — see the pitfall note.
            lineHeight: 1.3,
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            overflow: "hidden",
          }}
        >
          {episode.title || copy.episodeLabel(episode.episodeNumber, index)}
        </span>
        {/* Ternary, not `&&`: a duration of 0 is falsy AND a number, so `&&`
            would render a bare "0" under the episode title. */}
        {episode.durationSeconds ? (
          <span
            style={{
              display: "block",
              fontSize: 18,
              lineHeight: 1.4,
              color: style.inkDim,
            }}
          >
            {copy.duration(episode.durationSeconds)}
          </span>
        ) : null}
      </span>
    </button>
  );
}

// ── playing ─────────────────────────────────────────────────────────────────

/**
 * The one place a `<video>` element exists on this screen.
 *
 * Mounted only while `phase === "play"`, and unmounted the instant a guest
 * presses Back or the title ends. That is not tidiness: the panel's decoder pool
 * is single digits and the WebView does not hand one back when React removes a
 * node, so a surface kept alive off-screen is a room that plays three things and
 * then plays nothing until somebody reboots it.
 *
 * **The resolve is named when it fails.** Between the press and the first frame
 * there is no `StreamSurface` yet, so this component owns that window: it asks
 * the server what to play, bounds the wait, and prints
 * `STREAM_FAILURE_MESSAGE[failure]` if the answer never comes or is a refusal.
 * A spinner that never resolves is the failure mode this entire player was built
 * to remove, and it must not be reintroduced in the eight seconds before the
 * player exists.
 */
function PlayerStage({
  item,
  style,
  copy,
  scopeRef,
  hints,
  onStop,
}: {
  item: TvCatalogueItem;
  style: VodScreenStyle;
  copy: VodCopy;
  scopeRef: RefObject<HTMLElement>;
  hints: boolean;
  onStop: () => void;
}) {
  const [playback, setPlayback] = useState<TvPlayback | null>(null);
  const [failure, setFailure] = useState<StreamFailure | null>(null);
  const [code, setCode] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setPlayback(null);
    setFailure(null);
    setCode(null);
    void (async () => {
      try {
        const next = await resolveTvPlayback(item.assetId);
        if (!alive) return;
        setPlayback(next);
      } catch (e) {
        if (!alive) return;
        const named =
          e instanceof TvPlaybackRefused ? e.failure : ("unknown" as const);
        // Counted BY CODE, exactly as `StreamSurface` counts its own (PLAYER-17).
        // A resolve that never produced a surface is still a play that failed,
        // and a fleet console that only saw the surface's failures would show a
        // premise with a broken entitlement as perfectly healthy.
        playbackFailed(named);
        setFailure(named);
        setCode(e instanceof TvPlaybackRefused ? e.code : "unknown");
      }
    })();
    return () => {
      alive = false;
    };
  }, [item.assetId]);

  // One cell, one job: Back stops. OK is deliberately inert — a stray press on
  // a remote in a guest's lap must not toggle anything mid-film.
  useFocusGrid({
    rows: 1,
    cols: 1,
    scopeRef,
    onBack: onStop,
  });

  const mint = useMemo(() => mintLicenceFor(item.assetId), [item.assetId]);

  /**
   * What this television actually watched (`ANALYTICS-2`).
   *
   * The only witness there is: the server minted a play URL and never hears
   * again, which is exactly why `content_play_daily` had no source. Nothing is
   * reported until the playhead is seen to MOVE, so a title that resolves and
   * then refuses to decode counts as no play at all rather than as a play with
   * no watch time - see `lib/contentPlay.ts`.
   */
  const play = usePlayReport({
    kind: "vod",
    assetId: item.assetId,
    label: item.title,
  });

  /**
   * The Back hint, and why it goes away.
   *
   * A guest has to be told that Back is how they leave a film — nothing else on
   * this screen says so. But a chip parked over the bottom of a two-hour film
   * sits exactly where subtitles go, and a static bright element held on a hotel
   * panel all evening is what burn-in is. So it is shown while the title starts
   * and for a few seconds after, then removed.
   */
  const [showHint, setShowHint] = useState(true);
  useEffect(() => {
    if (!playback) return;
    const t = setTimeout(() => setShowHint(false), HINT_LINGER_MS);
    return () => clearTimeout(t);
  }, [playback]);

  return (
    <div
      data-testid="vod-player"
      style={{ position: "absolute", inset: 0, background: "#000" }}
    >
      {playback && (
        <StreamSurface
          url={playback.url}
          licence={playback.licence}
          mintLicence={playback.encrypted ? mint : undefined}
          // No `onFailed`: the surface owns everything that goes wrong AFTER it
          // exists — it names the failure on screen and counts it by code
          // already, and tearing it down here would replace a readable sentence
          // with a black rectangle. Back is how a guest leaves it.
          onProgress={play.onProgress}
          onEnded={() => {
            // The completion is reported BEFORE the unmount, because the
            // unmount is what flushes the tail and a completion that arrived
            // after it would be a second, empty session.
            play.onEnded();
            onStop();
          }}
        />
      )}
      {!playback && !failure && (
        <Overlay testid="vod-starting" style={style}>
          {copy.starting}
        </Overlay>
      )}
      {failure && (
        <Overlay
          testid="vod-play-failure"
          style={style}
          failure={failure}
          code={code}
        >
          {STREAM_FAILURE_MESSAGE[failure]}
        </Overlay>
      )}
      {hints && showHint && (
        <div
          style={{
            position: "absolute",
            left: SAFE_X,
            right: SAFE_X,
            bottom: SAFE_Y,
            color: style.inkDim,
          }}
        >
          <RemoteHint
            items={[
              {
                key: "←",
                label: copy.hint.stop,
                bg: "rgba(0,0,0,.55)",
                fg: "#fff",
                border: "1px solid rgba(255,255,255,.25)",
              },
            ]}
          />
        </div>
      )}
    </div>
  );
}

function Overlay({
  testid,
  style,
  failure,
  code,
  children,
}: {
  testid: string;
  style: VodScreenStyle;
  failure?: StreamFailure;
  code?: string | null;
  children: ReactNode;
}) {
  return (
    <div
      data-testid={testid}
      data-failure={failure}
      data-refusal={code ?? undefined}
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "0 8vw",
        textAlign: "center",
        fontFamily: style.font,
        fontSize: 34,
        fontWeight: 500,
        lineHeight: 1.45,
        color: "rgba(255,255,255,.92)",
        background: "rgba(0,0,0,.72)",
      }}
    >
      {children}
    </div>
  );
}

// ── chrome ──────────────────────────────────────────────────────────────────

function Heading({
  style,
  eyebrow,
  title,
}: {
  style: VodScreenStyle;
  eyebrow: string;
  title: string;
}) {
  return (
    <div style={{ flex: "0 0 auto", marginBottom: 26 }}>
      <div
        style={{
          fontSize: 17,
          letterSpacing: "0.28em",
          textTransform: "uppercase",
          color: style.accent,
          fontWeight: 600,
          marginBottom: 10,
          lineHeight: 1.4,
        }}
      >
        {eyebrow}
      </div>
      <h1
        style={{
          margin: 0,
          fontFamily: style.titleFont,
          fontWeight: 600,
          fontSize: 56,
          // 1.2, not tighter: this box clips, and a tight line-height slices the
          // descenders off g/y/p/j/q on a TV. See
          // docs/memory/global/pitfalls/tv-tight-line-height-clipping.md.
          lineHeight: 1.2,
          letterSpacing: "-0.01em",
        }}
      >
        {title}
      </h1>
    </div>
  );
}

function Note({
  style,
  testid = "vod-note",
  children,
}: {
  style: VodScreenStyle;
  testid?: string;
  children: ReactNode;
}) {
  return (
    <div
      data-testid={testid}
      style={{
        flex: "1 1 auto",
        display: "flex",
        alignItems: "center",
        maxWidth: 1100,
        fontSize: 30,
        lineHeight: 1.45,
        color: style.inkDim,
      }}
    >
      {children}
    </div>
  );
}

function Hints({
  style,
  items,
}: {
  style: VodScreenStyle;
  items: { key: string; label: string }[];
}) {
  return (
    <div style={{ flex: "0 0 auto", marginTop: 22, color: style.inkDim }}>
      <RemoteHint
        items={items.map((it) => ({
          ...it,
          bg: style.card,
          fg: style.ink,
          border: `1px solid ${style.rule}`,
        }))}
      />
    </div>
  );
}

function Chip({
  testid,
  style,
  selected,
  focused,
  onHover,
  onPress,
  children,
}: {
  testid: string;
  style: VodScreenStyle;
  selected: boolean;
  focused: boolean;
  onHover: () => void;
  onPress: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      data-testid={testid}
      aria-pressed={selected}
      aria-current={focused ? "true" : undefined}
      onMouseEnter={onHover}
      onClick={onPress}
      style={{
        appearance: "none",
        padding: "10px 22px",
        borderRadius: 999,
        border: `1px solid ${focused ? style.accent : style.rule}`,
        background: selected ? style.accent : style.card,
        color: selected ? style.onAccent : style.ink,
        fontFamily: "inherit",
        fontSize: 20,
        fontWeight: 600,
        lineHeight: 1.4,
        cursor: "pointer",
        boxShadow: focused ? `0 0 0 4px ${style.accent}55` : "none",
      }}
    >
      {children}
    </button>
  );
}

function ActionButton({
  testid,
  style,
  focused,
  onPress,
  children,
}: {
  testid: string;
  style: VodScreenStyle;
  focused: boolean;
  onPress: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      data-testid={testid}
      aria-current={focused ? "true" : undefined}
      onClick={onPress}
      style={{
        appearance: "none",
        padding: "18px 44px",
        borderRadius: style.radius,
        border: `1px solid ${focused ? style.accent : style.rule}`,
        background: focused ? style.accent : style.card,
        color: focused ? style.onAccent : style.ink,
        fontFamily: "inherit",
        fontSize: 28,
        fontWeight: 700,
        lineHeight: 1.35,
        cursor: "pointer",
        boxShadow: focused ? `0 0 0 5px ${style.accent}55` : "none",
      }}
    >
      {children}
    </button>
  );
}
